import dayjs from 'dayjs'
import { v4 } from 'uuid'

import { Premise, Project } from '@/api/models/auth/lib/auth.types'
import { RequestByIdApiType } from '@/api/models/requests'
import { DEFAULT_DATE_ONLY_FORMAT } from '@/components/entities/requests/model/constants'
import { ParkingType } from '@/components/entities/requests/model/form.types'
import {
  AutosApiData,
  AutosFormData,
  PersonsApiData,
  PersonsFormData
} from '@/components/entities/requests/ui/model/schemas'
import { UploadedFileType } from '@/components/shared/file-uploader'

export const atLeastChars = (count: number) => `Не менее ${count} символов`

export const getPersonalAccountsByRoomId = (
  roomId?: string,
  projectData?: Project
) => {
  if (!roomId || !projectData) {
    return
  }

  return projectData.personalAccounts
    .filter(personalAccount =>
      personalAccount.premises.some(premise => premise.id === roomId)
    )
    .map(({ id, name, type }) => ({ id, name, type }))
}

export const formatAutosData = (
  data: AutosFormData,
  description?: string
): AutosApiData => {
  return {
    ...data,
    ...(description && { description }),
    autos: data?.autos?.map(auto => ({
      ...auto,
      number: auto.number ?? '',
      parkingPlace: auto.parkingPlace?.id || null
    }))
  }
}

export const formatPersonsData = (
  data: PersonsFormData,
  description?: string
): PersonsApiData => {
  return {
    ...data,
    ...(description && { description }),
    persons: data.persons.map(person => person.value ?? '')
  }
}

export const getDefaultAutos = (
  pass: RequestByIdApiType | undefined,
  parkingPlaces: ParkingType[] | undefined
) => {
  if (!parkingPlaces?.length) {
    return (
      pass?.autos?.map(auto => ({
        number: auto.number,
        model: auto.model || undefined,
        isStandard: auto.isStandard
      })) || [{ number: '', model: '', isStandard: true }]
    )
  }

  return pass?.autos?.length
    ? pass.autos.map(auto => {
        const parkingObject = parkingPlaces.find(
          parkingPlace => parkingPlace.id === auto.parkingPlace
        )
        return {
          ...auto,
          parkingPlace: parkingObject || null,
          model: auto.model || undefined,
          isStandard: auto.isStandard
        }
      })
    : [
        {
          number: '',
          model: '',
          parkingPlace: parkingPlaces[0],
          isStandard: true
        }
      ]
}

export const getDefaultPersons = (pass: RequestByIdApiType | undefined) => {
  return pass?.persons?.length
    ? pass?.persons?.map(person => ({ value: person }))
    : [{ value: '' }]
}

export const getDefaultRequestFiles = (
  request?: RequestByIdApiType
): UploadedFileType[] => {
  if (!request?.files?.length) {
    return []
  }

  return request.files.map(file => ({
    id: v4(),
    file: new File([], file.name),
    url: file.link,
    loading: false,
    progress: 100,
    error: false
  }))
}

export const getDefaultRoom = (
  rooms: Premise[],
  premiseId?: string
): Premise | undefined => {
  if (premiseId) {
    return rooms.find(room => room.id === premiseId)
  }
  return rooms[0]
}

export const getDesiredDate = (date: unknown): string => {
  if (date && typeof date === 'string') {
    return dayjs(date).isAfter(dayjs())
      ? date
      : dayjs().format(DEFAULT_DATE_ONLY_FORMAT)
  }

  return dayjs().format(DEFAULT_DATE_ONLY_FORMAT)
}
